// Where a list was, per history entry: its scroll position and its own state
// (tab, search words, sort, open children). Coming back to the same entry
// brings the list back as it was left.
//
// Where only the detail fits (a phone, a narrow window, a detail folded to
// full width) opening a row takes the list away, and coming back draws it
// anew: a new box starts at the top and a new screen at its first tab. The
// old web kept the list's position per history entry (2026-10-08, "going
// back from a Case or a chat to the list puts it back at the top"); its tabs
// were in the URL, which they no longer are (URL rule, 2026-10-16), so they
// are kept here too.
//
// Promise:
// - Back (the browser's, the top left's, the Dock's "up" and "back to the
//   list" when the list is the entry before) to the same entry: the list's
//   box goes back to where it was, its state to what it was, before paint.
// - A new entry for the list (the sidebar, the Dock, a link): from the top,
//   as a list opened anew.
// - While the list stays on screen (beside the detail) moving between
//   entries changes nothing; each entry keeps what was on screen when it was
//   left.
// - Rows read page by page that are not in yet when it comes back: the box
//   keeps going to the place as the rows come in (and the next pages are
//   read as the bottom comes near), until it is there, the person scrolls,
//   or a few seconds pass.
// - Kept for this window while it is open, at most LIMIT entries. Across a
//   start (a reload, the phone dropping the app and starting it again) only
//   the page shown and the one before it are kept, on the device, as the page
//   is put away and a moment after each change: started again on the same page (within a day), its lists
//   come back where they were; the first move to the page before (the way
//   back up to the list) brings that one back too. Signing out forgets it
//   (`forgetListPlaces`).
// - "Back to the list" goes back over the entry when the entry before is the
//   list (the old web, 2026-09-26: list → detail → list did not keep
//   growing the history); otherwise it opens the list as a new entry.
import { type RouterHistory, useRouter } from "@tanstack/react-router";
import { useMeApp } from "../app/context";
import { PERSISTED_MAX_AGE_MS } from "../data/persist";

/** Entries kept (the oldest is dropped first). */
const LIMIT = 100;

/** How long a position not reachable yet is waited for. */
const RESTORE_WAIT_MS = 4000;

type Slot = () => unknown;

export interface ListMemory {
  /** Starts following the app's history (once, when the router is made). */
  attach(history: RouterHistory): void;
  /** The current entry's key (null before `attach`). */
  key(): string | null;
  /** What was kept for this list's `name` in the current entry. */
  read(list: string, name: string): unknown;
  /** Keeps a value for this list's `name` in the current entry. */
  write(list: string, name: string, value: unknown): void;
  /**
   * Shows a value that lives on screen (a box's scroll position). When the
   * history moves while it is shown, the new entry keeps what is on screen.
   */
  live(list: string, name: string, get: Slot): () => void;
  /** The address of the entry before the current one, if this window saw it. */
  previousHref(): string | null;
  /**
   * The nearest entry before the current one whose address is not `inside`,
   * and how many entries back it is; null when this window did not see one.
   */
  before(
    inside: (href: string) => boolean,
  ): { href: string; steps: number } | null;
}

const slotKey = (list: string, name: string) => `${list}\u0000${name}`;

/** Where the places of the page last shown are kept on the device. */
const KEPT_KEY = "trameria:list-places";
/** How long after a change (a scroll) the places are kept. */
const KEEP_AFTER_MS = 300;

interface KeptPage {
  path: string;
  slots: [string, unknown][];
}

interface Kept {
  at: number;
  page: KeptPage;
  before: KeptPage | null;
}

const pathOf = (href: string) => href.split(/[?#]/)[0] ?? href;

// A list's state may hold a set (the rows open): JSON keeps it as a tagged array.
const encode = (_: string, v: unknown) =>
  v instanceof Set ? { $set: [...v] } : v;
const decode = (_: string, v: unknown) =>
  v !== null &&
  typeof v === "object" &&
  Array.isArray((v as { $set?: unknown }).$set)
    ? new Set((v as { $set: unknown[] }).$set)
    : v;

function deviceStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Forgets the places kept across starts (signing out: the next person's lists start anew). */
export function forgetListPlaces(storage = deviceStorage()): void {
  try {
    storage?.removeItem(KEPT_KEY);
  } catch {
    // Nothing kept.
  }
}

export interface ListMemoryOptions {
  /** Where the places are kept across starts (null: not kept). The device's by default. */
  storage?: Storage | null;
  now?: () => number;
}

export function createListMemory(options: ListMemoryOptions = {}): ListMemory {
  const storage =
    options.storage === undefined ? deviceStorage() : options.storage;
  const now = options.now ?? Date.now;
  let history: RouterHistory | null = null;
  const entries = new Map<string, Map<string, unknown>>();
  const lives = new Map<string, Slot>();
  // Index of an entry in this window's history → its address (the browser
  // does not tell the one before), and its key.
  const trail = new Map<number, string>();
  const keys = new Map<number, string>();
  // The page before the one started on, kept from the last start: brought
  // back when the window first moves there.
  let upLater: KeptPage | null = null;

  const keyOf = (h: RouterHistory) => {
    const s = h.location.state;
    return s.__TSR_key ?? s.key ?? null;
  };
  const write = (key: string, slot: string, value: unknown) => {
    let entry = entries.get(key);
    if (entry) entries.delete(key);
    else entry = new Map();
    // Back in at the end: the most recent last.
    entries.set(key, entry);
    entry.set(slot, value);
    while (entries.size > LIMIT) {
      const oldest = entries.keys().next().value;
      if (oldest === undefined) break;
      entries.delete(oldest);
    }
  };
  const record = (h: RouterHistory) => {
    const index = h.location.state.__TSR_index;
    trail.set(index, h.location.href);
    const key = keyOf(h);
    if (key !== null) keys.set(index, key);
  };
  /** Puts kept values in an entry, where it has none of its own. */
  const seed = (key: string, slots: [string, unknown][]) => {
    for (const [slot, value] of slots)
      if (!entries.get(key)?.has(slot)) write(key, slot, value);
  };
  const slotsOf = (key: string | undefined, onScreen: boolean) => {
    const slots = new Map(key ? entries.get(key) : undefined);
    if (onScreen) for (const [slot, get] of lives) slots.set(slot, get());
    return [...slots];
  };
  const readKept = (): Kept | null => {
    try {
      const raw = storage?.getItem(KEPT_KEY);
      if (!raw) return null;
      const kept = JSON.parse(raw, decode) as Kept;
      if (typeof kept?.at !== "number" || !kept.page) return null;
      return now() - kept.at <= PERSISTED_MAX_AGE_MS ? kept : null;
    } catch {
      return null;
    }
  };
  /** Keeps the page shown and the one before it for the next start. */
  const keep = () => {
    if (!storage || !history) return;
    const index = history.location.state.__TSR_index;
    const beforeHref = trail.get(index - 1);
    const kept: Kept = {
      at: now(),
      page: {
        path: pathOf(history.location.href),
        slots: slotsOf(keyOf(history) ?? undefined, true),
      },
      before:
        beforeHref === undefined
          ? null
          : {
              path: pathOf(beforeHref),
              slots: slotsOf(keys.get(index - 1), false),
            },
    };
    try {
      storage.setItem(KEPT_KEY, JSON.stringify(kept, encode));
    } catch {
      // Not kept: the lists start anew next time.
    }
  };
  // Kept again a moment after each change too: closed from the app
  // switcher, the phone ends the app without telling the page it was put
  // away (the person, 2026-11-02: a list came back at its top).
  let keepTimer: ReturnType<typeof setTimeout> | undefined;
  const keepSoon = () => {
    if (!storage) return;
    clearTimeout(keepTimer);
    keepTimer = setTimeout(keep, KEEP_AFTER_MS);
  };

  return {
    attach(h) {
      history = h;
      record(h);
      const kept = readKept();
      const startPath = pathOf(h.location.href);
      const startKey = keyOf(h);
      if (kept && startKey !== null && kept.page.path === startPath)
        seed(startKey, kept.page.slots);
      upLater = kept?.before ?? null;
      h.subscribe(() => {
        record(h);
        // What is on screen now is what the new entry shows.
        const key = keyOf(h);
        if (key === null) return;
        for (const [slot, get] of lives) write(key, slot, get());
        const path = pathOf(h.location.href);
        if (upLater && path !== startPath) {
          if (upLater.path === path) seed(key, upLater.slots);
          upLater = null;
        }
        keepSoon();
      });
      // Put away (another app, the phone locked, the page closed): the
      // places are kept, as the phone may drop the app from there.
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") keep();
        });
        window.addEventListener("pagehide", keep);
      }
    },
    key: () => (history ? keyOf(history) : null),
    read(list, name) {
      const key = history ? keyOf(history) : null;
      if (key === null) return undefined;
      return entries.get(key)?.get(slotKey(list, name));
    },
    write(list, name, value) {
      const key = history ? keyOf(history) : null;
      if (key === null) return;
      write(key, slotKey(list, name), value);
      keepSoon();
    },
    live(list, name, get) {
      const slot = slotKey(list, name);
      lives.set(slot, get);
      return () => {
        if (lives.get(slot) === get) lives.delete(slot);
      };
    },
    previousHref() {
      if (!history) return null;
      return trail.get(history.location.state.__TSR_index - 1) ?? null;
    },
    before(inside) {
      if (!history) return null;
      const now = history.location.state.__TSR_index;
      for (let i = now - 1; i >= 0; i--) {
        const href = trail.get(i);
        if (href === undefined) return null;
        if (!inside(href)) return { href, steps: now - i };
      }
      return null;
    },
  };
}

/**
 * Goes to `to` (a path of this app) as going back when the entry before is
 * that place: back over the entry, so the place comes back as it was left.
 * Otherwise it opens `to` as a new entry.
 */
export function useBackTo(): (to: string) => void {
  const { listMemory } = useMeApp();
  const router = useRouter();
  return (to) => {
    if (listMemory.previousHref() === router.buildLocation({ to }).href)
      router.history.back();
    else void router.navigate({ to });
  };
}

/**
 * Leaves a place made of several entries (settings: the index and its items)
 * for where the person was before entering it: back over every entry inside,
 * so that place comes back as it was left (the old web, 2026-10-18: "the
 * settings' back means leaving the settings, not back inside them"). When
 * this window did not see one (opened by its address, reloaded), `fallback`
 * as a new entry.
 */
export function useLeave(prefix: string, fallback: string): () => void {
  const { listMemory } = useMeApp();
  const router = useRouter();
  return () => {
    const inside = (href: string) => {
      const path = href.split(/[?#]/)[0];
      return path === prefix || path.startsWith(`${prefix}/`);
    };
    const before = listMemory.before(inside);
    if (before) router.history.go(-before.steps);
    else void router.navigate({ to: fallback });
  };
}

/**
 * Takes a box back to `top` and keeps at it while what it scrolls grows
 * (rows still coming in), until it is there, the person moves it, or
 * RESTORE_WAIT_MS pass. Returns the stop, and `pending()`: whether it is
 * still going (scroll events then are its own, not the person's).
 */
export function restoreScroll(
  box: HTMLElement,
  top: number,
): { stop(): void; pending(): boolean } {
  const there = () => Math.abs(box.scrollTop - top) <= 1;
  box.scrollTop = top;
  if (there() || typeof ResizeObserver === "undefined")
    return { stop: () => {}, pending: () => false };
  let going = true;
  const sizes = new ResizeObserver(() => {
    box.scrollTop = top;
    if (there()) stop();
  });
  const watch = (el: Element) => sizes.observe(el);
  for (const child of box.children) watch(child);
  // Rows put in later (a page read in) are watched too.
  const added = new MutationObserver((records) => {
    for (const r of records)
      for (const n of r.addedNodes) if (n instanceof Element) watch(n);
    box.scrollTop = top;
    if (there()) stop();
  });
  added.observe(box, { childList: true });
  // The person takes over: what they do wins.
  const inputs = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
  const timer = setTimeout(() => stop(), RESTORE_WAIT_MS);
  function stop() {
    if (!going) return;
    going = false;
    clearTimeout(timer);
    sizes.disconnect();
    added.disconnect();
    for (const t of inputs) box.removeEventListener(t, stop);
  }
  for (const t of inputs) box.addEventListener(t, stop, { passive: true });
  return { stop, pending: () => going };
}
